/**
 * Mock blog articles. Short realistic stubs per user choice.
 * Full content is intentionally lightweight and can be replaced by the future
 * backend CMS response without any UI changes.
 */

const paragraph = (t) => `<p>${t}</p>`;

export const mockBlogs = [
    {
        id: "blog-01",
        title: "New vs Used Shipping Containers: Which Should You Choose?",
        slug: "new-vs-used-shipping-containers",
        excerpt:
            "New containers offer a clean, uniform starting point while used units bring value for money. Here is how to weigh both against your project needs.",
        content: [
            paragraph(
                "New shipping containers are supplied unmodified and clean, giving you a predictable starting point for storage, cargo work or a full modification project. Used containers, on the other hand, provide a lower-cost option when appearance is secondary to function.",
            ),
            paragraph(
                "If you plan to repaint the unit, add windows or convert the container into an office, either grade can work — but keep in mind that heavily used containers may need repairs before modification. Talk to our team about your intended use before deciding.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.unsplash.com/photo-1706499856012-14f062c72b49?auto=format&fit=crop&w=1200&q=80",
        featuredImageAlt: "New and used containers stacked in a Singapore yard",
        author: "Container Specialist",
        category: "Buying Guides",
        categorySlug: "buying-guides",
        tags: ["new containers", "used containers", "buying guide"],
        featured: true,
        status: "published",
        publishedAt: "2024-11-04T00:00:00Z",
        updatedAt: "2024-11-04T00:00:00Z",
        readingTime: 3,
        seo: {
            title: "New vs Used Shipping Containers: How to Choose | Container Specialist",
            description:
                "Compare new and used shipping containers to select the right option for storage, cargo use or modification.",
            keywords: ["new vs used container", "shipping container buying guide"],
            slug: "new-vs-used-shipping-containers",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-02",
        title: "20ft vs 40ft Containers: How to Select the Right Size",
        slug: "20ft-vs-40ft-containers",
        excerpt:
            "Size affects storage capacity, delivery access and cost. A quick guide to choosing between 20ft and 40ft configurations.",
        content: [
            paragraph(
                "A 20ft container fits into tighter spaces and is easier to place on constrained sites. A 40ft container roughly doubles the internal footprint, making it a strong choice when you need more storage volume or larger workspaces.",
            ),
            paragraph(
                "Access, crane availability and long-term reuse should be considered before choosing. If in doubt, share your site details with us over WhatsApp.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.pexels.com/photos/32865462/pexels-photo-32865462.jpeg?auto=compress&cs=tinysrgb&w=1200",
        featuredImageAlt: "20ft and 40ft containers side by side",
        author: "Container Specialist",
        category: "Container Sizes",
        categorySlug: "container-sizes",
        tags: ["20ft", "40ft", "sizing"],
        featured: false,
        status: "published",
        publishedAt: "2024-10-20T00:00:00Z",
        updatedAt: "2024-10-20T00:00:00Z",
        readingTime: 2,
        seo: {
            title: "20ft vs 40ft Container Sizing Guide | Container Specialist",
            description:
                "Understand the differences between 20ft and 40ft containers to pick the right size for your requirements.",
            keywords: ["20ft container", "40ft container", "container sizes"],
            slug: "20ft-vs-40ft-containers",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-03",
        title: "What to Check Before Buying a Used Shipping Container",
        slug: "what-to-check-before-buying-used-container",
        excerpt:
            "A short checklist of things to look at when selecting a used container so you can avoid surprises later.",
        content: [
            paragraph(
                "Check the door mechanism, floor condition, wall integrity and seals. Small dents are normal on a used unit — structural damage is not.",
            ),
            paragraph(
                "Ask about how the container has previously been used, and whether repairs have been carried out. Photos of the specific unit are always useful before delivery.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.pexels.com/photos/13427763/pexels-photo-13427763.jpeg?auto=compress&cs=tinysrgb&w=1200",
        featuredImageAlt: "Detailed close-up of a used container door",
        author: "Container Specialist",
        category: "Buying Guides",
        categorySlug: "buying-guides",
        tags: ["used containers", "inspection", "buying guide"],
        featured: false,
        status: "published",
        publishedAt: "2024-10-02T00:00:00Z",
        updatedAt: "2024-10-02T00:00:00Z",
        readingTime: 3,
        seo: {
            title: "Used Container Buying Checklist | Container Specialist",
            description: "A short buyer's checklist for used shipping containers.",
            keywords: ["used container checklist", "buying used container"],
            slug: "what-to-check-before-buying-used-container",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-04",
        title: "Common Uses for Shipping Containers in Singapore",
        slug: "common-uses-for-shipping-containers-singapore",
        excerpt:
            "From site storage to workers' facilities and retail concepts — an overview of practical container applications.",
        content: [
            paragraph(
                "Beyond straightforward storage, containers are widely used as site offices, guard houses, workers' quarters, sanitary facilities, event spaces and even small-format retail concepts.",
            ),
            paragraph(
                "Local factors such as site access, ground conditions and regulatory approvals will influence what is possible in your project. Contact us to discuss your case in detail.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.pexels.com/photos/21533662/pexels-photo-21533662.jpeg?auto=compress&cs=tinysrgb&w=1200",
        featuredImageAlt: "Modified shipping container used as a building",
        author: "Container Specialist",
        category: "Industry Insights",
        categorySlug: "industry-insights",
        tags: ["applications", "container uses", "Singapore"],
        featured: false,
        status: "published",
        publishedAt: "2024-09-14T00:00:00Z",
        updatedAt: "2024-09-14T00:00:00Z",
        readingTime: 2,
        seo: {
            title: "Common Container Applications in Singapore | Container Specialist",
            description: "Common uses for shipping containers in Singapore — storage, offices, workers' quarters and more.",
            keywords: ["container uses Singapore", "container applications"],
            slug: "common-uses-for-shipping-containers-singapore",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-05",
        title: "How Shipping Containers Can Be Converted Into Site Offices",
        slug: "converting-containers-into-site-offices",
        excerpt:
            "A summary of what happens when a plain container is turned into a working office — doors, windows, wiring and services.",
        content: [
            paragraph(
                "Container offices typically involve fitting a personnel door, windows, insulation, internal lining, electrical wiring, lighting and provision for air-conditioning.",
            ),
            paragraph(
                "Depending on your usage, meeting rooms, toilet cubicles and pantry areas can be planned within a 40ft footprint.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.unsplash.com/photo-1655720845450-b3f2df8b562b?auto=format&fit=crop&w=1200&q=80",
        featuredImageAlt: "Container converted into an office space",
        author: "Container Specialist",
        category: "Container Offices",
        categorySlug: "container-offices",
        tags: ["office", "conversion", "customisation"],
        featured: false,
        status: "published",
        publishedAt: "2024-08-30T00:00:00Z",
        updatedAt: "2024-08-30T00:00:00Z",
        readingTime: 3,
        seo: {
            title: "Container Office Conversion Guide | Container Specialist",
            description: "How shipping containers can be converted into functional site offices.",
            keywords: ["container office conversion", "site office"],
            slug: "converting-containers-into-site-offices",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-06",
        title: "Container Customisation Options for Commercial Projects",
        slug: "container-customisation-options-commercial",
        excerpt:
            "Doors, windows, insulation, wiring and finishes — a snapshot of common customisation options for commercial projects.",
        content: [
            paragraph(
                "Common commercial customisations include roller shutter doors, custom counters, cladding, brand paintwork and signage panels.",
            ),
            paragraph(
                "Feasibility depends on the intended use, structural considerations and regulations. Share your concept with us and we'll advise on options.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.unsplash.com/photo-1655720845450-b3f2df8b562b?auto=format&fit=crop&w=1200&q=80",
        featuredImageAlt: "Container being customised for a commercial use",
        author: "Container Specialist",
        category: "Customisation",
        categorySlug: "customisation",
        tags: ["customisation", "commercial", "retail"],
        featured: false,
        status: "published",
        publishedAt: "2024-08-05T00:00:00Z",
        updatedAt: "2024-08-05T00:00:00Z",
        readingTime: 2,
        seo: {
            title: "Container Customisation for Commercial Use | Container Specialist",
            description: "Common customisation options for commercial container projects.",
            keywords: ["commercial container customisation"],
            slug: "container-customisation-options-commercial",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-07",
        title: "How to Prepare Your Site Before Container Delivery",
        slug: "prepare-site-before-container-delivery",
        excerpt:
            "Ground conditions, access and lifting equipment all matter. A short primer to help your delivery go smoothly.",
        content: [
            paragraph(
                "Prepare firm, level ground where the container will be placed. Consider truck and crane access, overhead clearance and any restrictions on your site.",
            ),
            paragraph(
                "Share photos of your delivery location when requesting a quote — it helps us confirm feasibility upfront.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1200&q=80",
        featuredImageAlt: "Container being prepared for site placement",
        author: "Container Specialist",
        category: "Project Planning",
        categorySlug: "project-planning",
        tags: ["delivery", "site preparation"],
        featured: false,
        status: "published",
        publishedAt: "2024-07-18T00:00:00Z",
        updatedAt: "2024-07-18T00:00:00Z",
        readingTime: 2,
        seo: {
            title: "Site Preparation Before Container Delivery | Container Specialist",
            description: "How to prepare your site before your container arrives.",
            keywords: ["container delivery Singapore", "site preparation"],
            slug: "prepare-site-before-container-delivery",
            schemaType: "BlogPosting",
        },
    },
    {
        id: "blog-08",
        title: "Questions to Ask a Container Supplier Before Requesting a Quote",
        slug: "questions-to-ask-container-supplier",
        excerpt:
            "A short list of practical questions that will save time when you contact a container supplier.",
        content: [
            paragraph(
                "Ask about container condition, availability, delivery, customisation feasibility and typical timelines. A good supplier will happily walk you through the options.",
            ),
            paragraph(
                "Where possible, come prepared with the intended use, preferred size, quantity and delivery location.",
            ),
        ].join("\n"),
        featuredImage:
            "https://images.pexels.com/photos/32865462/pexels-photo-32865462.jpeg?auto=compress&cs=tinysrgb&w=1200",
        featuredImageAlt: "Container supplier discussing a project",
        author: "Container Specialist",
        category: "Buying Guides",
        categorySlug: "buying-guides",
        tags: ["buying guide", "supplier", "questions"],
        featured: false,
        status: "published",
        publishedAt: "2024-06-24T00:00:00Z",
        updatedAt: "2024-06-24T00:00:00Z",
        readingTime: 2,
        seo: {
            title: "Questions to Ask a Container Supplier | Container Specialist",
            description: "Practical questions to ask before requesting a container quote.",
            keywords: ["container supplier questions", "container quote guide"],
            slug: "questions-to-ask-container-supplier",
            schemaType: "BlogPosting",
        },
    },
];

export const mockBlogCategories = [
    { slug: "buying-guides", name: "Buying Guides" },
    { slug: "container-sizes", name: "Container Sizes" },
    { slug: "customisation", name: "Customisation" },
    { slug: "container-offices", name: "Container Offices" },
    { slug: "project-planning", name: "Project Planning" },
    { slug: "industry-insights", name: "Industry Insights" },
];
